import type { ApiResponse } from '@bandos/shared'

/**
 * Handles authentication errors (401, TOKEN_EXPIRED, UNAUTHORIZED)
 * Clears auth tokens and redirects to login page
 */
export function handleAuthError(
  response: Response,
  errorData: { message?: string; code?: string; details?: unknown }
): ApiResponse<never> | null {
  if (
    response.status !== 401 &&
    errorData.code !== 'TOKEN_EXPIRED' &&
    errorData.code !== 'UNAUTHORIZED'
  ) {
    return null
  }

  if (typeof window === 'undefined') {
    return {
      data: null,
      error: {
        message: errorData.message ?? 'Authentication required',
        status: response.status,
        code: errorData.code ?? 'UNAUTHORIZED',
        details: errorData.details as Record<string, unknown> | undefined,
      },
    }
  }

  const currentPath = window.location.pathname

  // Don't redirect if already on login page or on public pages
  if (
    currentPath === '/login' ||
    currentPath.startsWith('/view/') ||
    currentPath.startsWith('/vote/') ||
    currentPath.startsWith('/s/')
  ) {
    return {
      data: null,
      error: {
        message: errorData.message ?? 'Authentication required',
        status: response.status,
        code: errorData.code ?? 'UNAUTHORIZED',
        details: errorData.details as Record<string, unknown> | undefined,
      },
    }
  }

  // Check if redirect is already in progress (prevent multiple redirects)
  const isAuthRedirecting = sessionStorage.getItem('authRedirecting') === 'true'
  if (isAuthRedirecting) {
    return {
      data: null,
      error: {
        message: errorData.message ?? 'Session expired',
        status: response.status,
        code: errorData.code ?? 'UNAUTHORIZED',
        details: errorData.details as Record<string, unknown> | undefined,
      },
    }
  }

  // Set redirect flag
  sessionStorage.setItem('authRedirecting', 'true')

  // Clear auth tokens from localStorage
  localStorage.removeItem('supabase.auth.token')
  // Clear all Supabase auth keys
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('sb-') || key.includes('supabase')) {
      localStorage.removeItem(key)
    }
  })

  // Clear session storage (except the redirect flag we just set)
  const authRedirectFlag = sessionStorage.getItem('authRedirecting')
  sessionStorage.clear()
  if (authRedirectFlag) {
    sessionStorage.setItem('authRedirecting', authRedirectFlag)
  }

  // Redirect to login
  window.location.href = '/app/login'

  // Return immediately since we're redirecting
  return {
    data: null,
    error: {
      message: errorData.message ?? 'Session expired. Please log in again.',
      status: response.status,
      code: errorData.code ?? 'UNAUTHORIZED',
      details: errorData.details as Record<string, unknown> | undefined,
    },
  }
}
